'use client';

import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';

import Link from 'next/link';

import { Button } from '@components/Button';
import { Input } from '@components/Input';
import { TextGroup } from '@components/TextGroup';

import { ButtonWrapper, InputWrapper, RequestResetWrapper } from './styles';
interface ResetPasswordFormData {
  email: string;
}

interface RequestResetPasswordFormProps {
  onSubmit: (formData: ResetPasswordFormData) => Promise<void>;
  hasError: boolean;
  activate: boolean;
}

/**
 * Request Reset Password Form component renders a form for users to request a password reset
 */
export function RequestResetPasswordForm({
  onSubmit,
  hasError,
  activate = false,
}: RequestResetPasswordFormProps) {
  const { t } = useTranslation('common');
  const [email, setEmail] = useState('');

  const handleFormSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onSubmit({ email });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  return (
    <RequestResetWrapper onSubmit={handleFormSubmit}>
      <TextGroup
        label={activate ? t('request_activate.welcome') : t('request_reset_password.label')}
        header={
          activate ? t('request_activate.activateAccount') : t('request_reset_password.title')
        }
        paragraph={
          activate
            ? t('request_activate.activateAccountDescription')
            : t('request_reset_password.description')
        }
      />
      <InputWrapper>
        <Input
          type="email"
          id="email"
          placeholder={t('login.enterEmail') + '...'}
          name="email"
          label={t('email')}
          value={email}
          onChange={handleInputChange}
          hasError={hasError}
          required={true}
          errorLabel={email ? t('errors.userNotFound') : t('errors.emailRequired')}
        />
      </InputWrapper>
      <ButtonWrapper>
        <Button
          type="submit"
          label={activate ? t('request_activate.activateAccountLabel') : t('reset')}
          variant={'primary'}
        />
        <Link href="/login" passHref>
          <Button type="button" label={t('backToLogin')} variant={'secondary'} />
        </Link>
      </ButtonWrapper>
    </RequestResetWrapper>
  );
}
